import { API, graphqlOperation, Hub } from 'aws-amplify';
import { useEffect, useRef } from 'react';
import type Observable from 'zen-observable-ts';
import { onEmployeeDashboardUpdate } from '@gym-graphql/subscriptions';
import type { KisiEvent } from '@gym-src/API';

interface DashboardSubscriptionHandlers {
  onEvents: (events: Array<KisiEvent | null>) => void;
  onRecovered: (isCurrent: () => boolean) => Promise<boolean>;
}

type DashboardSubscriptionMessage = {
  value: { data: { onEmployeeDashboardUpdate: Array<KisiEvent | null> } };
};

const retryDelay = (attempt: number) =>
  Math.min(1000 * 2 ** Math.min(attempt, 5), 30000) + Math.floor(Math.random() * 1000);

const isDashboardAcknowledgment = (event: string, data: unknown): boolean => {
  if (event !== 'Subscription ack' || typeof data !== 'object' || !data) return false;
  const query = 'query' in data ? (data as { query: unknown }).query : undefined;
  return (
    typeof query === 'string' &&
    query.replace(/\s+/g, '') === onEmployeeDashboardUpdate.replace(/\s+/g, '')
  );
};

export const useEmployeeDashboardSubscription = (
  siteLocationId: number,
  doorId: number,
  handlers: DashboardSubscriptionHandlers
): void => {
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    let disposed = false;
    let generation = 0;
    let connectionAttempts = 0;
    let catchUpAttempts = 0;
    let connected = false;
    let needsCatchUp = false;
    let catchUpInFlight = false;
    let subscription: { unsubscribe: () => void } | undefined;
    let retryTimer: number | undefined;
    let catchUpTimer: number | undefined;

    const clearTimers = () => {
      window.clearTimeout(retryTimer);
      window.clearTimeout(catchUpTimer);
      retryTimer = undefined;
      catchUpTimer = undefined;
    };

    const catchUp = async () => {
      if (disposed || !connected || !needsCatchUp || catchUpInFlight) return;
      catchUpInFlight = true;
      const currentGeneration = generation;
      const isCurrent = () => !disposed && connected && generation === currentGeneration;
      let recovered = false;
      try {
        recovered = await handlersRef.current.onRecovered(isCurrent);
      } catch {
        console.error('Unable to recover employee dashboard events', { siteLocationId, doorId });
      }
      catchUpInFlight = false;
      if (disposed) return;
      if (!isCurrent()) {
        if (connected) void catchUp();
        return;
      }
      if (recovered) {
        needsCatchUp = false;
        catchUpAttempts = 0;
        return;
      }
      catchUpTimer = window.setTimeout(() => {
        catchUpTimer = undefined;
        void catchUp();
      }, retryDelay(catchUpAttempts));
      catchUpAttempts += 1;
    };

    const acknowledge = () => {
      if (disposed || connected || !subscription) return;
      connected = true;
      connectionAttempts = 0;
      void catchUp();
    };

    const disconnect = () => {
      generation += 1;
      connected = false;
      subscription?.unsubscribe();
      subscription = undefined;
      clearTimers();
    };

    const recover = () => {
      if (disposed) return;
      needsCatchUp = true;
      disconnect();
      if (!navigator.onLine) return;
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        connect();
      }, retryDelay(connectionAttempts));
      connectionAttempts += 1;
    };

    const connect = () => {
      if (disposed) return;
      disconnect();
      const currentGeneration = generation;
      const isCurrent = () => !disposed && generation === currentGeneration;
      try {
        const nextSubscription = ((API.graphql(
          graphqlOperation(onEmployeeDashboardUpdate)
        ) as unknown) as Observable<DashboardSubscriptionMessage>).subscribe({
          next: ({ value }) => {
            if (!isCurrent()) return;
            acknowledge();
            handlersRef.current.onEvents(value.data.onEmployeeDashboardUpdate);
          },
          error: () => {
            if (isCurrent()) recover();
          },
          complete: () => {
            if (isCurrent()) recover();
          }
        });
        if (isCurrent()) subscription = nextSubscription;
        else nextSubscription.unsubscribe();
      } catch {
        console.error('Unable to start employee dashboard subscription', {
          siteLocationId,
          doorId
        });
        if (isCurrent()) recover();
      }
    };

    // Amplify 3 emits this event when AppSync acknowledges the subscription,
    // even on a quiet door with no new check-ins to confirm recovery.
    const stopListening = Hub.listen('api', ({ payload }) => {
      if (isDashboardAcknowledgment(payload.event, payload.data)) acknowledge();
    });
    const onOnline = () => {
      if (needsCatchUp && !connected) connect();
    };
    window.addEventListener('offline', recover);
    window.addEventListener('online', onOnline);
    if (navigator.onLine) connect();
    else needsCatchUp = true;

    return () => {
      disposed = true;
      disconnect();
      stopListening();
      window.removeEventListener('offline', recover);
      window.removeEventListener('online', onOnline);
    };
  }, [siteLocationId, doorId]);
};
